import React, { useState, useEffect } from 'react';
import { BookOpen, Target, GitFork, Users, Droplets, HeartPulse, Home, Globe, Zap, CheckCircle, Award } from 'lucide-react';
import { t, getCurrentLanguage } from '../utils/translations';
import Navbar from '../components/Navabar';
import Footer from '../components/Footer';
import api from '../components/api';

const MissionPage = () => {
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());
  const [missionData, setMissionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Écouter les changements de langue
  useEffect(() => {
    // Détecter la langue depuis l'URL au chargement initial
    const detectLanguageFromURL = () => {
      const path = window.location.pathname;
      if (path.startsWith('/en/')) {
        return 'en';
      } else if (path.startsWith('/fr/')) {
        return 'fr';
      }
      return 'fr'; // Par défaut
    };

    const initialLang = detectLanguageFromURL();
    setCurrentLanguage(initialLang);
    
    // Forcer la mise à jour de i18n si nécessaire
    if (window.i18n && window.i18n.language !== initialLang) {
      window.i18n.changeLanguage(initialLang);
      localStorage.setItem('i18nextLng', initialLang);
    }

    const handleLanguageChange = () => {
      const newLang = getCurrentLanguage();
      setCurrentLanguage(newLang);
    };

    // Écouter l'événement de changement de langue
    window.addEventListener('languageChanged', handleLanguageChange);

    // Écouter aussi les changements i18n
    const i18nListener = () => {
      const newLang = getCurrentLanguage();
      setCurrentLanguage(newLang);
    };

    if (window.i18n) {
      window.i18n.on('languageChanged', i18nListener);
    }

    // Écouter les changements d'URL
    const handleURLChange = () => {
      const urlLang = detectLanguageFromURL();
      if (urlLang !== currentLanguage) {
        setCurrentLanguage(urlLang);
        if (window.i18n && window.i18n.language !== urlLang) {
          window.i18n.changeLanguage(urlLang);
        }
      }
    };

    window.addEventListener('popstate', handleURLChange);

    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('popstate', handleURLChange);
      if (window.i18n) {
        window.i18n.off('languageChanged', i18nListener);
      }
    };
  }, [currentLanguage]);

  // Récupérer les données de la page Mission
  useEffect(() => {
    const fetchMissionData = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/mission-page/');
        setMissionData(response.data);
        setError(null);
      } catch (err) {
        setError('Erreur lors du chargement de la page');
        console.error('Erreur API Mission:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMissionData();
  }, []);

  // Fonction pour obtenir le contenu traduit
  const getTranslatedContent = (frContent, enContent) => {
    if (currentLanguage === 'en' && enContent) {
      return enContent;
    }
    return frContent || enContent || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#089556] mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Erreur de chargement</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button 
              onClick={() => {
                setLoading(true);
                setError(null);
                api.get('/api/mission-page/')
                  .then(response => {
                    setMissionData(response.data);
                    setError(null);
                  })
                  .catch(err => {
                    setError('Erreur lors du chargement de la page');
                    console.error('Erreur API Mission:', err);
                  })
                  .finally(() => setLoading(false));
              }}
              className="bg-[#089556] text-white px-6 py-2 rounded-lg hover:bg-[#0a7c4a] transition-colors"
            >
              Réessayer
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Enhanced Hero Section */}
      <section className="relative h-[400px] mb-16 overflow-hidden">
        {/* Enhanced Molecular Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#089556] via-[#0b6f47] to-[#07D1EB]">
          <div className="absolute inset-0 opacity-[0.3]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                radial-gradient(circle at 20% 30%, rgba(255,255,255,0.8) 3px, transparent 3px),
                radial-gradient(circle at 80% 70%, rgba(255,255,255,0.6) 2.5px, transparent 2.5px),
                radial-gradient(circle at 40% 80%, rgba(255,255,255,0.7) 2px, transparent 2px),
                radial-gradient(circle at 70% 20%, rgba(255,255,255,0.5) 3.5px, transparent 3.5px),
                radial-gradient(circle at 90% 50%, rgba(255,255,255,0.8) 2.8px, transparent 2.8px),
                radial-gradient(circle at 30% 60%, rgba(255,255,255,0.6) 2.2px, transparent 2.2px)
              `,
              backgroundSize: '200px 200px, 160px 160px, 220px 220px, 180px 180px, 190px 190px, 150px 150px'
            }}></div>
            
            {/* Enhanced Connection Lines */}
            <div className="absolute inset-0" style={{
              backgroundImage: `
                linear-gradient(35deg, transparent 48%, rgba(255,255,255,0.4) 49%, rgba(255,255,255,0.4) 51%, transparent 52%),
                linear-gradient(-35deg, transparent 48%, rgba(255,255,255,0.3) 49%, rgba(255,255,255,0.3) 51%, transparent 52%),
                linear-gradient(70deg, transparent 48%, rgba(255,255,255,0.35) 49%, rgba(255,255,255,0.35) 51%, transparent 52%)
              `,
              backgroundSize: '350px 350px, 320px 320px, 380px 380px',
              opacity: '0.5'
            }}></div>
          </div>

          {/* Enhanced Floating Molecules */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-1/6 left-1/4 w-5 h-5 bg-white rounded-full opacity-20 animate-pulse shadow-lg shadow-white/40"></div>
            <div className="absolute top-2/3 right-1/4 w-4 h-4 bg-white rounded-full opacity-30 animate-bounce shadow-lg shadow-white/50"></div>
            <div className="absolute top-1/2 left-3/4 w-4.5 h-4.5 bg-white rounded-full opacity-25 animate-pulse shadow-lg shadow-white/45"></div>
            <div className="absolute top-1/4 right-1/3 w-3 h-3 bg-white rounded-full opacity-35 animate-bounce shadow-lg shadow-white/40"></div>
            <div className="absolute bottom-1/6 left-1/6 w-3.5 h-3.5 bg-white rounded-full opacity-25 animate-pulse shadow-lg shadow-white/50"></div>
          </div>
        </div>

        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center relative z-10">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mb-8 shadow-xl border border-white/30">
              <Target className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl font-black text-white tracking-tight drop-shadow-lg mb-4">
              {getTranslatedContent(missionData?.hero_title_fr, missionData?.hero_title_en)}
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto leading-relaxed drop-shadow-sm">
              {getTranslatedContent(missionData?.hero_subtitle_fr, missionData?.hero_subtitle_en)}
            </p>
            <div className="w-32 h-1 bg-white/60 mx-auto mt-6 rounded-full"></div>
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        {/* Enhanced Contexte Actuel Section */}
        <section className="relative mb-20 overflow-hidden">
          {/* Molecular Background */}
          <div className="absolute inset-0 opacity-[0.08]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                radial-gradient(circle at 25% 25%, #089556 2px, transparent 2px),
                radial-gradient(circle at 75% 75%, #07D1EB 1.5px, transparent 1.5px),
                radial-gradient(circle at 50% 10%, #089556 1px, transparent 1px)
              `,
              backgroundSize: '150px 150px, 120px 120px, 180px 180px'
            }}></div>
          </div>

          <div className="relative z-10 bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20">
            <div className="flex items-center gap-6 mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-full shadow-lg">
                <BookOpen className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-4xl font-bold text-[#089556]">
                  {getTranslatedContent(missionData?.context_title_fr, missionData?.context_title_en)}
                </h2>
                <div className="w-20 h-1 bg-gradient-to-r from-[#089556] to-[#07D1EB] rounded-full mt-2"></div>
              </div>
            </div>
            <div className="prose prose-lg max-w-none text-gray-700 text-justify">
              <div dangerouslySetInnerHTML={{ 
                __html: getTranslatedContent(missionData?.context_content_fr, missionData?.context_content_en)
              }} />
            </div>
          </div>
        </section>

        {/* Enhanced Vision Section */}
        <section className="relative mb-20 overflow-hidden">
          {/* Molecular Background */}
          <div className="absolute inset-0 opacity-[0.06]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                radial-gradient(circle at 80% 20%, #07D1EB 2px, transparent 2px),
                radial-gradient(circle at 20% 80%, #089556 1.5px, transparent 1.5px)
              `,
              backgroundSize: '160px 160px, 140px 140px'
            }}></div>
          </div>

          <div className="relative z-10 bg-gradient-to-br from-[#089556]/10 via-[#089556]/5 to-[#07D1EB]/10 p-8 rounded-2xl shadow-lg border border-white/20">
            <div className="flex items-center gap-6 mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-full shadow-lg">
                <Globe className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-4xl font-bold text-[#089556]">
                  {getTranslatedContent(missionData?.vision_title_fr, missionData?.vision_title_en)}
                </h2>
                <div className="w-20 h-1 bg-gradient-to-r from-[#089556] to-[#07D1EB] rounded-full mt-2"></div>
              </div>
            </div>
            <div className="prose prose-lg max-w-none text-gray-700 text-justify space-y-6">
              <div dangerouslySetInnerHTML={{ 
                __html: getTranslatedContent(missionData?.vision_content_fr, missionData?.vision_content_en)
              }} />
              <div className="grid md:grid-cols-3 gap-4 mt-6">
                {(getTranslatedContent(missionData?.vision_items_fr, missionData?.vision_items_en) || []).map((item, index) => (
                  <div key={index} className="flex items-center gap-3 text-gray-700 bg-white/60 backdrop-blur-sm p-4 rounded-xl shadow-sm">
                    <CheckCircle className="h-5 w-5 text-[#089556] flex-shrink-0" />
                    <span className="text-justify">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Enhanced Les 5 Piliers d'Action Section */}
        <section className="relative mb-20 overflow-hidden">
          {/* Molecular Background */}
          <div className="absolute inset-0 opacity-[0.05]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                radial-gradient(circle at 40% 60%, #089556 2px, transparent 2px),
                radial-gradient(circle at 60% 40%, #07D1EB 1.5px, transparent 1.5px)
              `,
              backgroundSize: '180px 180px, 150px 150px'
            }}></div>
          </div>

          <div className="relative z-10">
            <div className="flex items-center gap-6 mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-full shadow-lg">
                <Zap className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-4xl font-bold text-[#089556]">
                  {getTranslatedContent(missionData?.pillars_title_fr, missionData?.pillars_title_en)}
                </h2>
                <div className="w-24 h-1 bg-gradient-to-r from-[#089556] to-[#07D1EB] rounded-full mt-2"></div>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(getTranslatedContent(missionData?.pillars_items_fr, missionData?.pillars_items_en) || []).map((pillar, index) => (
                <div key={index} className="group relative bg-white/90 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300">
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${pillar.gradient} rounded-t-2xl`}></div>
                  
                  <div className="text-[#089556] mb-4 mt-2">
                    {pillar.icon === 'Users' && <Users className="h-8 w-8" />}
                    {pillar.icon === 'BookOpen' && <BookOpen className="h-8 w-8" />}
                    {pillar.icon === 'Droplets' && <Droplets className="h-8 w-8" />}
                    {pillar.icon === 'HeartPulse' && <HeartPulse className="h-8 w-8" />}
                    {pillar.icon === 'Home' && <Home className="h-8 w-8" />}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3 text-justify">{pillar.title}</h3>
                  <ul className="space-y-2 text-gray-600">
                    {pillar.points?.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-[#07D1EB] mr-2">•</span>
                        <span className="text-justify">{point}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Decorative Elements */}
                  <div className="absolute top-4 right-4 w-8 h-8 opacity-10">
                    <div className="w-2 h-2 bg-[#07D1EB] rounded-full absolute top-0 left-0 animate-pulse"></div>
                    <div className="w-1.5 h-1.5 bg-[#089556] rounded-full absolute top-2 right-0 animate-pulse" style={{animationDelay: '0.5s'}}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Enhanced Stratégie Opérationnelle Section */}
        <section className="relative mb-20 overflow-hidden">
          {/* Molecular Background */}
          <div className="absolute inset-0 opacity-[0.05]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                radial-gradient(circle at 60% 40%, #089556 2px, transparent 2px),
                radial-gradient(circle at 40% 60%, #07D1EB 1.5px, transparent 1.5px)
              `,
              backgroundSize: '160px 160px, 130px 130px'
            }}></div>
          </div>

          <div className="relative z-10">
            <div className="flex items-center gap-6 mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-full shadow-lg">
                <GitFork className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-4xl font-bold text-[#089556]">
                  {getTranslatedContent(missionData?.strategy_title_fr, missionData?.strategy_title_en)}
                </h2>
                <div className="w-24 h-1 bg-gradient-to-r from-[#089556] to-[#07D1EB] rounded-full mt-2"></div>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-[#089556]/10 via-[#089556]/5 to-[#07D1EB]/10 p-8 rounded-2xl shadow-lg border border-white/20">
                <h3 className="text-2xl font-semibold text-[#089556] mb-6 flex items-center gap-3">
                  <Zap className="h-6 w-6" />
                  {getTranslatedContent(missionData?.change_axes_title_fr, missionData?.change_axes_title_en)}
                </h3>
                <ul className="space-y-4">
                  {(getTranslatedContent(missionData?.change_axes_items_fr, missionData?.change_axes_items_en) || []).map((item, i) => (
                    <li key={i} className="flex items-start gap-4 text-gray-700">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-r from-[#089556] to-[#07D1EB] flex items-center justify-center text-white shadow-lg">
                        {item.icon === 'GitFork' && <GitFork className="h-5 w-5" />}
                        {item.icon === 'Users' && <Users className="h-5 w-5" />}
                        {item.icon === 'Zap' && <Zap className="h-5 w-5" />}
                      </div>
                      <span className="text-justify font-medium">{item.label}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gradient-to-br from-[#07D1EB]/10 via-[#07D1EB]/5 to-[#089556]/10 p-8 rounded-2xl shadow-lg border border-white/20">
                <h3 className="text-2xl font-semibold text-[#089556] mb-6 flex items-center gap-3">
                  <Award className="h-6 w-6" />
                  {getTranslatedContent(missionData?.catalysts_title_fr, missionData?.catalysts_title_en)}
                </h3>
                <ul className="space-y-4">
                  {(getTranslatedContent(missionData?.catalysts_items_fr, missionData?.catalysts_items_en) || []).map((item, i) => (
                    <li key={i} className="flex items-start gap-4 text-gray-700">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-r from-[#07D1EB] to-[#089556] flex items-center justify-center text-white font-bold shadow-lg">
                        {i + 1}
                      </div>
                      <span className="text-justify font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Enhanced Objectif 2026 Section */}
        <section className="relative mb-20 overflow-hidden">
          {/* Molecular Background */}
          <div className="absolute inset-0 opacity-[0.08]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                radial-gradient(circle at 30% 70%, #089556 2px, transparent 2px),
                radial-gradient(circle at 70% 30%, #07D1EB 1.5px, transparent 1.5px)
              `,
              backgroundSize: '170px 170px, 140px 140px'
            }}></div>
          </div>

          <div className="relative z-10 text-center py-12 bg-gradient-to-br from-[#089556]/15 via-[#089556]/10 to-[#07D1EB]/15 rounded-2xl shadow-lg border border-white/20">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-full mb-8 shadow-xl">
              <Target className="h-10 w-10 text-white" />
            </div>
            <h3 className="text-3xl font-bold text-[#089556] mb-6">
              {getTranslatedContent(missionData?.objective_2026_title_fr, missionData?.objective_2026_title_en)}
            </h3>
            <div className="w-24 h-1 bg-gradient-to-r from-[#089556] to-[#07D1EB] mx-auto mb-6 rounded-full"></div>
            <div className="max-w-4xl mx-auto">
              <div className="text-xl text-gray-700 mb-8 leading-relaxed text-justify">
                <div dangerouslySetInnerHTML={{ 
                  __html: getTranslatedContent(missionData?.objective_2026_content_fr, missionData?.objective_2026_content_en)
                }} />
              </div>
              
              {/* Stats Cards */}
              <div className="grid md:grid-cols-2 gap-6 mt-8">
                {(getTranslatedContent(missionData?.objective_2026_stats_fr, missionData?.objective_2026_stats_en) || []).map((stat, index) => (
                  <div key={index} className="bg-white/80 backdrop-blur-sm p-6 rounded-xl shadow-lg border border-white/20">
                    <div className="text-3xl font-bold text-[#089556] mb-2">{stat.value}</div>
                    <div className="text-gray-700 font-medium text-justify">{stat.label}</div>
                    <div className="text-sm text-gray-500 mt-1 text-justify">{stat.subtitle}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute top-6 right-6 w-12 h-12 opacity-10">
              <div className="w-3 h-3 bg-[#07D1EB] rounded-full absolute top-0 left-0 animate-pulse"></div>
              <div className="w-2 h-2 bg-[#089556] rounded-full absolute top-3 right-0 animate-pulse" style={{animationDelay: '0.5s'}}></div>
              <div className="w-2.5 h-2.5 bg-[#07D1EB] rounded-full absolute bottom-0 left-3 animate-pulse" style={{animationDelay: '1s'}}></div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default MissionPage;
